import { useRef } from 'react';
import { Box, Flex } from '@storyofams/react-ui';
import {
  getPlainText,
  withStory,
  WithStoryProps,
} from '@storyofams/storyblok-toolkit';
import { GetStaticProps, NextPage } from 'next';
import { FAQPageJsonLd } from 'next-seo';
import SbEditable from 'storyblok-react';

import {
  Container,
  Seo,
  Heading,
  Accordion,
  RichText,
  Divider,
} from '~components';
import { enhancedStaticProps, withLayout } from '~lib';
import { TableLink, TableOfContents } from '~components/common/TableOfContents';
import { IntegrationpageItems } from '~lib/storyblok/sdk';

interface IntegrationsOverviewProps extends WithStoryProps {
  integrations: IntegrationpageItems;
}
const IntegrationsOverview: NextPage<IntegrationsOverviewProps> = ({
  story,
}) => {
  const { title, questions: categories } = story?.content;
  const tableLinksRef = useRef<TableLink[]>([]);
  let questions = [];

  categories?.forEach((category) =>
    category?.questions?.forEach((question) =>
      questions.push({
        questionName: question?.question,
        acceptedAnswerText: getPlainText(question?.answer),
      }),
    ),
  );

  return (
    <>
      <Seo story={story} />
      <FAQPageJsonLd mainEntity={questions} />

      <Flex>
        <Container>
          {story?.content?.title && (
            <SbEditable content={story?.content?.title}>
              <Heading variant="h2" as="h1" mb={6}>
                {title}
              </Heading>
            </SbEditable>
          )}

          {categories?.map((category) => {
            const link = `/faq#l${category?._uid}`;
            const linksInList = tableLinksRef.current.map(
              (tableLink) => tableLink.link,
            );

            if (!linksInList.includes(link)) {
              tableLinksRef.current.push({
                link,
                label: category?.title,
              });
            }
            return (
              <SbEditable
                content={category?.content || {}}
                key={category?._uid}
              >
                <Box
                  id={`l${category?._uid}`}
                  mb={8}
                  css={`
                    scroll-margin-top: 100px;
                  `}
                >
                  <Heading variant="h3" as="h2" mb={4.5}>
                    {category?.title}
                  </Heading>
                  {category?.questions?.map((q) => (
                    <Accordion title={q?.question} key={q?.question}>
                      <RichText text={q?.answer} />
                    </Accordion>
                  ))}
                  <Divider color="grey100" />
                </Box>
              </SbEditable>
            );
          })}
        </Container>

        <TableOfContents
          label="On this page"
          tableLinks={tableLinksRef.current}
        />
      </Flex>
    </>
  );
};

export default withLayout(withStory(IntegrationsOverview));

export const getStaticProps: GetStaticProps<IntegrationsOverviewProps> =
  enhancedStaticProps(async ({ sdk }) => {
    let story = null;
    let notFound = false;

    try {
      story = (await sdk.faq({ slug: 'faq/' })).FaqItem;
    } catch (e) {
      notFound = true;
    }

    return {
      props: {
        story,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  });
